import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  createTheme,
  ThemeProvider,
  Box,
  Toolbar,
  Container,
  Grid,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
  SelectChangeEvent,
} from "@mui/material";
import { Add, ArrowBack } from "@mui/icons-material";
import {
  getVehicleById,
  updateVehicle,
  getColorsVehicle,
  getModelsVehicle,
  getModelYear,
  getStorageLocation,
} from "../../../api/vehicleApi";
import {
  ColorData,
  Models,
  StorageLocation,
  YearModel,
} from "../../../types/vehicleData";
import FormTextField from "../novoVeiculo/formTextField";
import CreateModal from "../novoVeiculo/createModal";
import { LoadingButton } from "@mui/lab";

const defaultTheme = createTheme({
  typography: {
    fontFamily: "Poppins, sans-serif",
  },
});

const styles = {
  formControl: {
    fullWidth: true,
    marginTop: -2,
    "& .MuiSelect-root": {
      borderColor: "#303030",
      borderRadius: 3,
      boxShadow: 6,
      "&:focus": {
        backgroundColor: "#ffddec",
        borderColor: "brown",
      },
      "&:before": {
        borderColor: "orange",
      },
      "&:after": {
        borderColor: "green",
      },
    },
    ".MuiOutlinedInput-notchedOutline": {
      borderColor: "#303030",
      borderRadius: 3,
      boxShadow: 6,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#303030",
      color: "#303030",
      borderWidth: "2px",
    },
  },
  button: {
    height: 50,
    marginTop: 2,
    backgroundColor: "#303030",
    borderRadius: 2,
    alignItems: "center",
    ":hover": {
      bgcolor: "#5c5c5c",
    },
  },
};

enum FormField {
  VIN = "V.I.N",
  Placa = "Placa",
  Modelo = "Modelo",
  Cor = "Cor",
  KM = "KM",
  RadioCode = "Código de Rádio",
  AnoModelo = "Ano / Modelo",
  DataArmazenagem = "Data Armazenagem",
  LocalArmazenagem = "Local Armazenagem",
  KMFinal = "KM Final",
  DataEntrega = "Data de Entrega",
}

interface FormValues {
  [FormField.VIN]: string;
  [FormField.Placa]: string;
  [FormField.Modelo]: string | number;
  [FormField.Cor]: string;
  [FormField.KM]: string;
  [FormField.RadioCode]: string;
  [FormField.AnoModelo]: string | number;
  [FormField.DataArmazenagem]: string;
  [FormField.LocalArmazenagem]: string;
  [FormField.KMFinal]: string;
  [FormField.DataEntrega]: string;
}

const EditVehiclePage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const token = localStorage.getItem("token");
  const [kmPhotos, setKmPhotos] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [radioPhotos, setRadioPhotos] = useState<string[]>([]);
  const [corOptions, setCorOptions] = useState<
    { id: number; descricao_cor: string }[]
  >([]);
  const [modelOptions, setModelOptions] = useState<
    { id: number; descricao_modelo: string }[]
  >([]);
  const [yearOptions, setYearOptions] = useState<
    { id: number; descricao_ano_modelo: string }[]
  >([]);
  const [localStorageOptions, setlocalStorageOptions] = useState<
    { id: number; descricao_local: string }[]
  >([]);
  const [formValues, setFormValues] = useState<FormValues>({
    [FormField.VIN]: "",
    [FormField.Placa]: "",
    [FormField.Modelo]: "",
    [FormField.Cor]: "",
    [FormField.KM]: "",
    [FormField.RadioCode]: "",
    [FormField.AnoModelo]: "",
    [FormField.DataArmazenagem]: "",
    [FormField.LocalArmazenagem]: "",
    [FormField.KMFinal]: "",
    [FormField.DataEntrega]: "",
  });
  const [openModal, setOpenModal] = useState<FormField | null>(null);
  const [capturedImages, setCapturedImages] = useState<string[]>([]);
  const [radioCapturedImages, setRadioCapturedImages] = useState<string[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (token && vehicleId) {
          const vehicleData = await getVehicleById(token, vehicleId);
          if (vehicleData !== null) {
            setFormValues({
              [FormField.VIN]: vehicleData.chassi_veiculo,
              [FormField.Placa]: vehicleData.placa_veiculo,
              [FormField.Modelo]: vehicleData.id_modelo.toString(),
              [FormField.Cor]: vehicleData.id_cor.toString(),
              [FormField.KM]: vehicleData.km_veiculo.toString(),
              [FormField.RadioCode]: vehicleData.codigo_radio_veiculo,
              [FormField.AnoModelo]: vehicleData.id_ano_modelo.toString(),
              [FormField.DataArmazenagem]: vehicleData.data_armazenagem_veiculo,
              [FormField.LocalArmazenagem]: vehicleData.id_local.toString(),
              [FormField.KMFinal]:
                vehicleData.km_final_veiculo !== null
                  ? vehicleData.km_final_veiculo.toString()
                  : "",
              [FormField.DataEntrega]:
                vehicleData.data_recebimento_veiculo !== null
                  ? vehicleData.data_recebimento_veiculo
                  : "",
            });
            setKmPhotos(
              vehicleData.fotos_km.map(
                (photo: { imagem_km_foto: any }) => photo.imagem_km_foto
              )
            );
            setRadioPhotos(
              vehicleData.fotos_codigo_radio.map(
                (photo: { imagem_codigo_radio_foto: any }) =>
                  photo.imagem_codigo_radio_foto
              )
            );
          } else {
            console.error("Dados do veículo não encontrados.");
          }
        }
      } catch (error) {
        console.error("Erro ao buscar detalhes do veículo:", error);
      }
    };

    const fetchSelect = async () => {
      try {
        if (token) {
          const colorsData: ColorData[] = await getColorsVehicle(token);
          const colors = colorsData.map((color) => ({
            id: color.id,
            descricao_cor: color.descricao_cor,
          }));

          const modelsVehicle: Models[] = await getModelsVehicle(token);
          const models = modelsVehicle.map((model) => ({
            id: model.id,
            descricao_modelo: model.descricao_modelo,
          }));

          const yearModels: YearModel[] = await getModelYear(token);
          const years = yearModels.map((year) => ({
            id: year.id,
            descricao_ano_modelo: year.descricao_ano_modelo,
          }));

          const storageLocation: StorageLocation[] = await getStorageLocation(
            token
          );
          const location = storageLocation.map((location) => ({
            id: location.id,
            descricao_local: location.descricao_local,
          }));

          setCorOptions(colors);
          setModelOptions(models);
          setYearOptions(years);
          setlocalStorageOptions(location);
        }
      } catch (error) {
        console.error("Erro ao buscar opções:", error);
      }
    };

    fetchData();
    fetchSelect();
  }, [token, vehicleId]);

  useEffect(() => {
    if (Object.values(formValues).some((value) => !!value)) {
      setIsEditing(true);
    }
  }, [formValues]);

  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | { name?: string; value: unknown }
    >
  ) => {
    if (typeof event.target === "object" && event.target.name) {
      const { name, value } = event.target;
      setFormValues((prevValues) => ({
        ...prevValues,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      if (!formValues["V.I.N"] && !formValues["Placa"]) {
        toast.error("Preencha pelo menos o V.I.N ou a Placa para continuar.");
        setLoading(false);
        return;
      }

      if (formValues["Placa"] && formValues["Placa"].length !== 7) {
        toast.error("A placa deve ter exatamente 7 dígitos.");
        setLoading(false);
        return;
      }

      if (token && vehicleId) {
        const corId = parseInt(formValues[FormField.Cor] as string);
        const modeloId = parseInt(formValues[FormField.Modelo] as string);
        const anoModeloId = parseInt(formValues[FormField.AnoModelo] as string);
        const storageLocationId = parseInt(
          formValues[FormField.LocalArmazenagem] as string
        );

        if (
          !isNaN(corId) &&
          !isNaN(modeloId) &&
          !isNaN(anoModeloId) &&
          !isNaN(storageLocationId)
        ) {
          const vehicleData = {
            data_recebimento_veiculo: formValues[FormField.DataEntrega],
            codigo_radio_veiculo: formValues[FormField.RadioCode],
            km_final_veiculo: parseInt(formValues[FormField.KMFinal]),
            data_armazenagem_veiculo: formValues[FormField.DataArmazenagem],
            id_local: parseInt(formValues[FormField.LocalArmazenagem]),
            id_cor: parseInt(formValues[FormField.Cor]),
          };

          await updateVehicle(token, vehicleId, vehicleData);
          toast.success("Veículo atualizado com sucesso!");
          navigate("/veiculos");
        }
      } else {
        toast.error("ID do veículo não encontrado!");
      }
    } catch (error) {
      toast.error("Não foi possível atualizar os detalhes do veículo!");
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setOpenModal(null);
  };

  const handleCreateModalOpen = (fieldName: FormField) => {
    setOpenModal(fieldName);
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: "flex" }}>
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ mb: 2, mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                "&:hover": {
                  cursor: "pointer",
                  color: "red",
                },
              }}
              onClick={() => navigate("/veiculos")}
            >
              <IconButton
                edge="start"
                color="inherit"
                aria-label="voltar"
                sx={{ color: "inherit" }}
              >
                <ArrowBack />
              </IconButton>
              <Typography
                variant="h6"
                component="div"
                sx={{ color: "inherit", mt: 0.2 }}
              >
                Voltar
              </Typography>
            </Box>
          </Toolbar>

          <Container sx={{ mb: 4 }} maxWidth={"lg"}>
            <Box component="form" noValidate>
              <Typography
                variant="h5"
                fontStyle={"inherit"}
                sx={{ fontWeight: 400 }}
              >
                Editar Veículo
                <Divider
                  variant="middle"
                  sx={{
                    width: 120,
                    backgroundColor: "red",
                    height: 2,
                    marginLeft: 0,
                  }}
                />
              </Typography>
              <Grid container spacing={3} mt={-2} p={2}>
                {Object.entries(formValues).map(([fieldName, fieldValue]) => (
                  <Grid item xs={12} sm={6} key={fieldName}>
                    {fieldName === FormField.Cor ||
                    fieldName === FormField.LocalArmazenagem ||
                    fieldName === FormField.Modelo ||
                    fieldName === FormField.AnoModelo ? (
                      <FormControl fullWidth sx={styles.formControl}>
                        <Typography ml={1} mt={2} mb={1}>
                          {fieldName}
                        </Typography>
                        <Select
                          labelId={`${fieldName}-label`}
                          id={fieldName}
                          value={fieldValue !== "" ? fieldValue : ""}
                          placeholder={`Selecione o ${fieldName}`}
                          disabled={
                            fieldName === FormField.Modelo ||
                            fieldName === FormField.AnoModelo
                          }
                          onChange={(
                            event: SelectChangeEvent<{ value: unknown }>
                          ) => {
                            const { value } = event.target;
                            setFormValues((prevValues) => ({
                              ...prevValues,
                              [fieldName]:
                                typeof value === "object"
                                  ? ((value as { value: unknown })
                                      .value as string)
                                  : (value as string),
                            }));
                          }}
                          displayEmpty={true}
                          sx={{
                            "& .MuiSelect-root": {
                              borderColor: "#303030",
                              borderRadius: 3,
                              boxShadow: 6,

                              "&:focus": {
                                backgroundColor: "#ffddec",
                                borderColor: "brown",
                              },
                              "&:before": {
                                borderColor: "orange",
                              },
                              "&:after": {
                                borderColor: "green",
                              },
                            },

                            ".MuiOutlinedInput-notchedOutline": {
                              borderColor: "#303030",
                              borderRadius: 3,
                              boxShadow: 6,
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#303030",
                              color: "#303030",
                              borderWidth: "2px",
                            },
                          }}
                        >
                          {fieldName !== FormField.Modelo && (
                            <MenuItem
                              onClick={() => handleCreateModalOpen(fieldName)}
                            >
                              <IconButton>
                                <Add />
                              </IconButton>
                              Cadastrar {fieldName}
                            </MenuItem>
                          )}
                          <MenuItem value="" disabled>
                            Selecione {`${fieldName}`}
                          </MenuItem>
                          {fieldName === FormField.Cor &&
                            corOptions.map((option, index) => (
                              <MenuItem key={index} value={option.id}>
                                {option.descricao_cor}
                              </MenuItem>
                            ))}
                          {fieldName === FormField.LocalArmazenagem &&
                            localStorageOptions.map((option, index) => (
                              <MenuItem key={index} value={option.id}>
                                {option.descricao_local}
                              </MenuItem>
                            ))}
                          {fieldName === FormField.Modelo &&
                            modelOptions.map((option, index) => (
                              <MenuItem
                                disabled={fieldName === FormField.Modelo}
                                key={index}
                                value={option.id}
                              >
                                {option.descricao_modelo}
                              </MenuItem>
                            ))}
                          {fieldName === FormField.AnoModelo &&
                            yearOptions.map((option, index) => (
                              <MenuItem key={index} value={option.id}>
                                {option.descricao_ano_modelo}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <FormTextField
                        label={fieldName}
                        name={fieldName}
                        value={fieldValue}
                        onChange={handleChange}
                        onUpdateVIN={(vin) => {
                          setFormValues((prevValues) => ({
                            ...prevValues,
                            "V.I.N": vin,
                          }));
                        }}
                        isEditing={isEditing}
                        disabled={
                          fieldName === FormField.VIN ||
                          fieldName === FormField.KM ||
                          fieldName === FormField.Placa
                        }
                        capturedImages={capturedImages}
                        setCapturedImages={setCapturedImages}
                        radioCapturedImages={radioCapturedImages}
                        setRadioCapturedImages={setRadioCapturedImages}
                      />
                    )}
                    {fieldName === FormField.KM && (
                      <div>
                        {kmPhotos.map((photo, index) => (
                          <img
                            key={index}
                            src={`${process.env.REACT_APP_API_DOMAIN}/storage/${photo}`}
                            alt={`Foto KM ${index}`}
                            style={{
                              maxWidth: "80px",
                              marginTop: "10px",
                              marginLeft: "4px",
                            }}
                          />
                        ))}
                      </div>
                    )}
                    {fieldName === FormField.RadioCode && (
                      <div>
                        {radioPhotos.map((photo, index) => (
                          <img
                            key={index}
                            src={`${process.env.REACT_APP_API_DOMAIN}/storage/${photo}`}
                            alt={`Foto Código de Rádio ${index}`}
                            style={{
                              maxWidth: "80px",
                              marginTop: "10px",
                              marginLeft: "4px",
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </Grid>
                ))}
              </Grid>
            </Box>
            <LoadingButton
              variant="contained"
              loading={loading}
              fullWidth
              sx={styles.button}
              onClick={handleSubmit}
            >
              <Typography>Atualizar</Typography>
            </LoadingButton>
          </Container>
        </Box>
      </Box>
      {openModal && (
        <CreateModal
          open={true}
          fieldName={openModal}
          onClose={handleModalClose}
          setCorOptions={setCorOptions}
          setModelOptions={setModelOptions}
          setYearOptions={setYearOptions}
          setLocalStorageOptions={setlocalStorageOptions}
        />
      )}
    </ThemeProvider>
  );
};

export default EditVehiclePage;
